import {
  ADD_NEW_PRODUCT,
  CREATE_NEW_DEAL,
  DUPLICATE_DEAL,
  EDIT_DEAL_START,
  EDIT_DEAL_CANCEL,
  REMOVE_PRODUCT,
  TRIM_DEAL_FIELDS,
  UPDATE_DEAL_FIELD,
  UPDATE_PRODUCT_FIELD,
  UPDATE_NESTED_PRODUCT_FIELD,
  UPDATE_DEAL_DIRTY_STATUS,
  TFieldValue,
} from 'store/deal-edit/types';
import { EDealField, IDeal, EPayCycle, EPaymentTerm } from 'store/deals/types';
import { setSidebarVisibility, setSidebarHeaderVisibility } from 'store/sidebar/actions';
import { fromApi } from 'lib/helpers/deal-formatters';
import { setDealInResults, setIsStale } from 'store/deal-evaluation/actions';
import { EStorageField } from 'lib/storage';
import { IStoreState } from 'store';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';

export function setIsDealDirty(isDirty: boolean) {
  return {
    type: UPDATE_DEAL_DIRTY_STATUS,
    isDirty,
  };
}

export function addNewProduct() {
  return (dispatch: ThunkDispatch<void, void, AnyAction>) => {
    dispatch(setIsDealDirty(true));
    dispatch(setIsStale(true));
    dispatch({
      type: ADD_NEW_PRODUCT,
    });
  };
}

export function createNewDeal() {
  return (dispatch: ThunkDispatch<void, void, AnyAction>) => {
    dispatch(setSidebarVisibility(true));
    dispatch(setSidebarHeaderVisibility(true));

    dispatch({
      type: CREATE_NEW_DEAL,
    });
  };
}

export function duplicateDeal(deal: IDeal) {
  return (dispatch: ThunkDispatch<void, void, AnyAction>) => {
    dispatch(setSidebarVisibility(true));
    dispatch(setSidebarHeaderVisibility(true));
    dispatch({
      type: DUPLICATE_DEAL,
      deal,
    });
  };
}

export function editDealStart(deal: IDeal) {
  return (dispatch: ThunkDispatch<void, void, AnyAction>) => {
    dispatch(setSidebarVisibility(true));
    dispatch(setDealInResults(deal));
    dispatch(setSidebarHeaderVisibility(true));
    dispatch({
      type: EDIT_DEAL_START,
      deal: fromApi(deal),
    });
  };
}

export function editDealCancel() {
  return (dispatch: ThunkDispatch<void, void, AnyAction>) => {
    dispatch(setIsDealDirty(true));
    dispatch(setSidebarVisibility(false));
    dispatch(setSidebarHeaderVisibility(true));
    dispatch({ type: EDIT_DEAL_CANCEL });
  };
}

export function removeProduct(index: number) {
  return (dispatch: ThunkDispatch<void, void, AnyAction>) => {
    dispatch(setIsDealDirty(true));
    dispatch(setIsStale(true));
    dispatch({
      type: REMOVE_PRODUCT,
      index,
    });
  };
}

export function trimDealFields() {
  return {
    type: TRIM_DEAL_FIELDS,
  };
}

const checkEDealPeriodStartEndPayDelayChanges = (field: string) => {
  return field === EDealField.PERIOD_START || field === EDealField.PERIOD_END || field === EDealField.PAY_DELAY;
};

export function updateDealField(field: string, value: TFieldValue) {
  return (dispatch: ThunkDispatch<void, void, AnyAction>) => {
    // Mark deal as dirty
    dispatch(setIsDealDirty(true));

    // Check for changes that affect deal evaluation staleness
    if (
      checkEDealPeriodStartEndPayDelayChanges(field) ||
      field === EDealField.STANDARD_PAY_DELAY ||
      field === EDealField.PROBABILITY_OF_DEFAULT ||
      field === EDealField.ADDITIONAL_CREDIT_REQUIRMENT
    ) {
      dispatch(setIsStale(true));
    }

    // Update local storage for:
    // - Region
    // - Product Line
    if (field === EStorageField.PRODUCT_LINE || field === EStorageField.REGION) {
      localStorage.setItem(field, value.toString());
    }

    dispatch({
      type: UPDATE_DEAL_FIELD,
      field,
      value,
    });
  };
}

// Handles the click of a cost cycle
export function handleCreditPaymentTermCycleClick(payCycleClicked: EPayCycle, _paymentTerm: EPaymentTerm) {
  return (dispatch: ThunkDispatch<void, void, AnyAction>, getState: () => IStoreState) => {
    // Mark deal as dirty
    dispatch(setIsDealDirty(true));

    // Mark deal evaluation as stale
    dispatch(setIsStale(true));

    const { dealEdit } = getState();
    const { payCycle, stdPayCycle } = dealEdit;
    const fieldToUpdate = _paymentTerm === EPaymentTerm.PROPOSED ? EDealField.PAY_CYCLE : EDealField.STANDARD_PAY_CYCLE;
    let valueToUpdate = null;

    switch (_paymentTerm) {
      case EPaymentTerm.STANDARD: {
        if (payCycleClicked !== stdPayCycle) {
          valueToUpdate = payCycleClicked;
        }
        break;
      }

      case EPaymentTerm.PROPOSED: {
        if (payCycleClicked !== payCycle) {
          valueToUpdate = payCycleClicked;
        }
        break;
      }

      default: {
        break;
      }
    }

    // Update local storage
    localStorage.setItem(fieldToUpdate, valueToUpdate ?? '');

    dispatch({
      type: UPDATE_DEAL_FIELD,
      field: fieldToUpdate, // payCycle or stdPayCycle
      value: valueToUpdate, // "month" or "delivery"
    });
  };
}

export function updateProductField(
  index: number,
  fieldName: string,
  value: string | boolean | { enabled: boolean },
  nestedFieldName?: string
) {
  return (dispatch: ThunkDispatch<void, void, AnyAction>) => {
    // Mark deal as dirty
    dispatch(setIsDealDirty(true));

    // Mark evaluation as stale
    dispatch(setIsStale(true));

    // Update local storage for:
    // - Product
    // - Volume Unit
    if (fieldName === EStorageField.NAME || fieldName === EStorageField.VOLUME_UNIT) {
      localStorage.setItem(fieldName, typeof value === 'string' ? value : '');
    }

    // Handle nested field scenario
    if (nestedFieldName) {
      dispatch({
        type: UPDATE_NESTED_PRODUCT_FIELD,
        index,
        fieldName,
        nestedFieldName,
        value,
      });
    } else {
      dispatch({
        type: UPDATE_PRODUCT_FIELD,
        index,
        fieldName,
        value,
      });
    }
  };
}
